import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { LEAD_WEBHOOK_URL } from "../../config/webhooks";
import type { Salesperson, Lead } from "../../types/lead";
import type { Location } from "../../types/location";
import type { OrgLocation } from "../../config/locations";
import { getLocationsMerged } from "../../lib/locations/getLocations";
import { phoneDigitsUpTo10 } from "../../lib/lead/phones";
import { canAccessPrimaryMode, type PrimaryMode } from "./sections/LeadWorkspaceHeaderAccess";
import { supabase } from "@/lib/supabase";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import { canAccessByAnchor } from "@/lib/entitlements/modeAnchorRequirement";
import { resolveRoleBucketHome } from "@/lib/lastPrimaryModeStorage";
import { qcReportLinkPending } from "@/components/lead/useQcReportLink";

type UseLeadWorkspaceBootstrapParams = {
  auth: any;
  mode: PrimaryMode;
  setMode: Dispatch<SetStateAction<PrimaryMode>>;
  trainingMode: boolean;
  setTrainingMode: Dispatch<SetStateAction<boolean>>;
  isListening: boolean;
  currentLead: Lead | null;
  location: Location | null;
  salesperson: Salesperson | null;
  scopedOrganizationId: string | null;
  locations: OrgLocation[];
  locationsLoaded: boolean;
  setSalesperson: Dispatch<SetStateAction<Salesperson | null>>;
  setLocation: Dispatch<SetStateAction<Location | null>>;
  setLocations: Dispatch<SetStateAction<OrgLocation[]>>;
  setLocationsLoaded: Dispatch<SetStateAction<boolean>>;
};

export function useLeadWorkspaceBootstrap(params: UseLeadWorkspaceBootstrapParams) {
  const { anchors } = useAnchorEntitlements();

  const selectableLocations = useMemo(() => {
    if (params.auth?.role !== "sales") return params.locations;
    const allowed = new Set(
      (params.auth?.accessibleLocationIds ?? []).map((id: string) => String(id).trim()).filter(Boolean)
    );
    const active = params.auth?.activeLocationId?.trim();
    if (active) allowed.add(active);
    if (allowed.size === 0) return [];
    return params.locations.filter((l) => allowed.has(l.id));
  }, [
    params.auth?.role,
    params.auth?.accessibleLocationIds,
    params.auth?.activeLocationId,
    params.locations,
  ]);

  useEffect(() => {
    if (!params.locationsLoaded || !params.location) return;
    const stillValid = selectableLocations.some((l) => l.id === params.location?.id);
    if (!stillValid) params.setLocation(null);
  }, [params.locationsLoaded, params.location, params.setLocation, selectableLocations]);

  useEffect(() => {
    if (!params.auth) return;
    if (qcReportLinkPending()) return;
    if (params.auth.status !== "ready" && params.auth.status !== "no-supabase") return;
    const roleOk = canAccessPrimaryMode(params.mode, params.auth);
    const anchorOk = canAccessByAnchor(params.mode, anchors);
    if (roleOk && anchorOk) return;
    const fallback = resolveRoleBucketHome(params.auth, anchors);
    if (fallback && fallback !== params.mode) params.setMode(fallback);
  }, [params.auth, anchors, params.mode, params.setMode]);

  useEffect(() => {
    if (!params.auth?.session || !params.auth.profile) return;
    const profileName = params.auth.profile.full_name?.trim() || params.auth.profile.display_name?.trim() || "";
    const profileEmail = (params.auth.profile.email ?? params.auth.session.user.email ?? "").trim();
    const profileCell = phoneDigitsUpTo10(params.auth.profile.work_phone ?? params.auth.profile.personal_phone ?? "");
    const nameFromEmail = profileEmail.includes("@") ? profileEmail.split("@")[0]?.trim() || "" : "";
    const displayName = profileName || nameFromEmail || "Sales rep";
    params.setSalesperson((previous) => {
      const nextSalesperson: Salesperson = { name: displayName, email: profileEmail, cell: profileCell };
      if (previous) {
        if (
          nextSalesperson.name === previous.name &&
          nextSalesperson.email === previous.email &&
          nextSalesperson.cell === previous.cell
        ) {
          return previous;
        }
        return { ...previous, ...nextSalesperson };
      }
      return nextSalesperson;
    });
  }, [
    params.auth?.session,
    params.auth?.profile?.full_name,
    params.auth?.profile?.display_name,
    params.auth?.profile?.email,
    params.auth?.profile?.work_phone,
    params.auth?.profile?.personal_phone,
    params.auth?.session?.user?.email,
    params.setSalesperson,
  ]);

  useEffect(() => {
    if (params.auth?.session) return;
    if (params.auth?.status !== "ready" && params.auth?.status !== "no-supabase") return;
    params.setSalesperson((p) => p ?? { name: "Guest", email: "", cell: "" });
  }, [params.auth?.session, params.auth?.status, params.setSalesperson]);

  useEffect(() => {
    if (params.location || !params.locationsLoaded || !selectableLocations.length) return;
    const preferredId = params.auth?.activeLocationId?.trim() || "";
    if (preferredId) {
      const match = selectableLocations.find((l) => l.id === preferredId);
      if (match) {
        params.setLocation({
          id: match.id,
          name: match.fullName,
          address: match.address ?? "",
          phone: match.phone ?? "",
        });
        return;
      }
    }
    if (selectableLocations.length === 1) {
      const only = selectableLocations[0]!;
      params.setLocation({
        id: only.id,
        name: only.fullName,
        address: only.address ?? "",
        phone: only.phone ?? "",
      });
    }
  }, [params.auth?.activeLocationId, params.location, params.locationsLoaded, params.setLocation, selectableLocations]);

  const [ghlWebhookUrl, setGhlWebhookUrl] = useState<string>(LEAD_WEBHOOK_URL);
  useEffect(() => {
    const orgId = params.scopedOrganizationId;
    if (!orgId) {
      setGhlWebhookUrl(LEAD_WEBHOOK_URL);
      return;
    }
    if (!supabase) {
      setGhlWebhookUrl("");
      return;
    }
    setGhlWebhookUrl("");
    let cancelled = false;
    void supabase
      .from("ghl_connections")
      .select("webhook_url")
      .eq("organization_id", orgId)
      .eq("is_active", true)
      .not("webhook_url", "is", null)
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("[ghl] webhook load error", error.message);
          setGhlWebhookUrl("");
          return;
        }
        const url = (data as { webhook_url: string | null } | null)?.webhook_url?.trim();
        setGhlWebhookUrl(url || "");
      });
    return () => {
      cancelled = true;
    };
  }, [params.scopedOrganizationId]);

  useEffect(() => {
    const nextTraining = params.mode === "training";
    if (params.trainingMode !== nextTraining) params.setTrainingMode(nextTraining);
  }, [params.mode, params.trainingMode, params.setTrainingMode]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const locs = await getLocationsMerged();
        if (!alive) return;
        params.setLocations(locs);
        params.setLocationsLoaded(true);
      } catch (e) {
        console.warn("Failed to load dynamic locations; falling back to static list if present", e);
        params.setLocationsLoaded(true);
      } finally {
        if (alive) params.setLocationsLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [params.setLocations, params.setLocationsLoaded]);

  // Note: the bootstrap previously emitted ~5 `setStatus(...)` calls here to
  // surface workflow prompts ("Loading your profile…", "Select your
  // location to continue", "Ready - press Listen to begin", etc.). Slice B
  // of docs/planning/activity_dock_workflow_facilitation.md §6 retired the
  // visible status paragraph and converted the only load-bearing string
  // ("Select your location to continue") into the role-gated amber banner
  // in `LeadsModeBanners`. Slice F of the same doc removes the now-dead
  // writes here. Trainer/owner/platform users — who used to see prompt
  // copy like "Ready - press New Customer to begin" that didn't belong
  // to their role — get cleaner chrome as a side effect.

  const refreshLocations = async (reason = "manual", opts?: { force?: boolean }) => {
    try {
      params.setLocationsLoaded(false);
      const locs = await getLocationsMerged({ force: !!opts?.force });
      params.setLocations(locs);
      console.log("[locations] refreshed", { reason, count: locs.length });
    } catch (e) {
      console.warn("[locations] refresh failed", e);
    } finally {
      params.setLocationsLoaded(true);
    }
  };

  return { selectableLocations, ghlWebhookUrl, refreshLocations };
}
